import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FiMapPin, FiPhone, FiMail, FiClock, FiSend, FiInstagram, FiFacebook, FiYoutube } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import userApi from '../../api/userApi';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await userApi.submitContactForm({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message
      });
      
      toast.success('Message sent successfully! We will get back to you soon.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error(error.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | Panchali Design Hub</title>
        <meta 
          name="description" 
          content="Get in touch with Panchali Design Hub for inquiries about our collections, custom orders, or any other questions." 
        />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header Section */}
        <div className="text-center mb-16">
          <h1 className="text-3xl font-light mb-4">Contact Us</h1>
          <div className="w-24 h-[2px] bg-gradient-to-r from-indigo-900 to-transparent mx-auto mb-6"></div>
          <p className="text-gray-600 max-w-2xl mx-auto">
            We'd love to hear from you. Please reach out to us for any inquiries about our collections, custom orders, or general questions.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div className="bg-white rounded-lg shadow-sm p-8">
              <h2 className="text-xl font-light mb-6">Get in Touch</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4 text-gray-600">
                  <a 
                    href="https://maps.google.com/place/Panchali+design+hub/@9.9753423,76.2783157,17z" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-indigo-900 transition-colors group"
                    aria-label="Get directions to Panchali Design Hub"
                  >
                    <FiMapPin className="h-6 w-6 mt-1 transition-transform group-hover:scale-110" />
                  </a>
                  <span>
                    Panchali Design Hub, <br />
                    Market Rd, near St. Teresa's College, <br />
                    Ernakulam, Kerala 682011
                  </span>
                </div>

                <a 
                  href="tel:+916238249819" 
                  className="flex items-center gap-4 text-gray-600 hover:text-indigo-900 transition-colors group"
                >
                  <FiPhone className="h-6 w-6 transition-transform group-hover:scale-110" />
                  <span>+91 62382 49819</span>
                </a>

                <a 
                  href="mailto:NaN" 
                  className="flex items-center gap-4 text-gray-600 hover:text-indigo-900 transition-colors group"
                >
                  <FiMail className="h-6 w-6 transition-transform group-hover:scale-110" />
                  <span>None</span>
                </a>

                <div className="flex items-start gap-4 text-gray-600">
                  <FiClock className="h-6 w-6 mt-1" />
                  <div>
                    <p className="font-medium mb-2">Business Hours</p>
                    <p>Sunday - Saturday: 10:00 AM - 9:00 PM</p>
                  </div>
                </div>
              </div>

              {/* Social Media Links */}
              <div className="mt-8">
                <h3 className="text-lg font-light mb-4">Connect With Us</h3>
                <div className="flex items-center gap-6">
                  {[
                    { icon: FiInstagram, href: "https://www.instagram.com/panchalidesigns/", label: "Instagram" },
                    { icon: FiFacebook, href: "https://www.facebook.com/people/Panchalidesigns-Hub/61552684619205/", label: "Facebook" },
                    { icon: FiYoutube, href: "https://www.youtube.com/@panchalidesignshub/addons", label: "YouTube" }
                  ].map((social, index) => (
                    <a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.label}
                      className="text-gray-600 hover:text-indigo-900 transition-colors transform hover:scale-110"
                    >
                      <social.icon className="h-6 w-6" />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="aspect-video rounded-lg overflow-hidden shadow-sm">
              <iframe
                title="Panchali Design Hub Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3929.452790691244!2d76.27831571479533!3d9.975342392857597!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3b080d2f14f872c7%3A0xffb67bb27bbf53cd!2sPanchali%20design%20hub!5e0!3m2!1sen!2sin!4v1647841289123!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
              ></iframe>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-lg shadow-sm p-8">
            <h2 className="text-xl font-light mb-6">Send Us a Message</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                  Subject *
                </label>
                <select
                  id="subject"
                  name="subject"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select a subject</option>
                  <option value="order">Order Inquiry</option>
                  <option value="custom">Custom Order</option>
                  <option value="return">Returns & Exchange</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows="4"
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-900 hover:bg-indigo-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FiSend className="h-5 w-5" />
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16 text-center">
          <h2 className="text-xl font-light mb-4">Have Questions?</h2>
          <p className="text-gray-600 mb-6">
            Check our frequently asked questions for quick answers about orders, shipping, returns, and more.
          </p>
          <Link
            to="/faq"
            className="inline-flex items-center px-6 py-3 border border-indigo-900 text-base font-medium rounded-md text-indigo-900 hover:bg-indigo-50 transition-colors"
          >
            View FAQs
          </Link>
        </div>
      </div>
    </>
  );
};

export default Contact;




